import { InlineProposal } from '@/components/proposals/InlineProposal';
import { ChainResultsPreview } from '@/lib/proposals/ChainResultsPreview';

import type { ChatProposalSummary } from './types';

const NO_VIEWS: never[] = [];

interface ChainProposalsProps {
  proposals: ChatProposalSummary[];
  orgId: number;
  /** Inline cards (flag on, or any builder conversation) vs preview cards. */
  showInline: boolean;
  isBuilderConversation: boolean;
  /** Whether this turn's run is the one in flight — fresh cards open expanded. */
  isFresh: boolean;
  /**
   * The proposal currently rendered beside the chat. Builder: the panel's
   * draft. Ordinary chat: the open (docked) proposal.
   */
  shownProposalId: number | null | undefined;
  onSelectProposal: (proposalId: number) => void;
}

/**
 * The proposals a chain turn created.
 *
 * Inline when the `inline_proposals` flag is on, or always in a builder
 * conversation — builder cards are selectors the side panel depends on, so
 * they must exist. Otherwise they fall back to `ChainResultsPreview` cards.
 *
 * In a builder conversation a card's action is SELECT, not approve/reject:
 * the panel beside the chat carries the Save that applies a change, so a card
 * offering its own approve/reject would split that single apply path across
 * two places. Selecting a card only changes which proposal's draft the panel
 * shows.
 */
export function ChainProposals({
  proposals,
  orgId,
  showInline,
  isBuilderConversation,
  isFresh,
  shownProposalId,
  onSelectProposal,
}: ChainProposalsProps) {
  if (proposals.length === 0) {
    return null;
  }

  if (!showInline) {
    return (
      <ChainResultsPreview proposals={proposals} dynamicViews={NO_VIEWS} />
    );
  }

  return proposals.map(proposal => (
    <InlineProposal
      key={proposal.id}
      proposalId={proposal.id}
      orgId={orgId}
      title={proposal.title}
      resolution={proposal.resolution}
      isFresh={isFresh}
      actionCount={proposal.action_count}
      isBuilderMode={isBuilderConversation}
      // "Showing in panel" means exactly that: this card's draft is the one
      // rendered. Builder reads it off the draft rather than re-deriving the
      // pick, so a resolved newest (no draft) or a stale click never badges a
      // card the panel is not showing.
      isSelected={proposal.id === shownProposalId}
      onSelect={isBuilderConversation ? onSelectProposal : undefined}
    />
  ));
}
